import React, { useState, useEffect, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Sidebar from '../Sidebar/Sidebar';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { useNavigate } from 'react-router-dom';
import '../pages.css';

const ERROR_TYPE_LABELS = {
  send_fail:  'WhatsApp Send Failed',
  fetch_fail: 'Lead Fetch Failed',
  db_fail:    'Database Error',
  api_fail:   'API Error',
  cron_fail:  'Cron Job Failed',
  other:      'Other',
};

const ERROR_TYPE_COLORS = {
  send_fail:  { background: 'rgba(239,68,68,0.15)',  color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' },
  fetch_fail: { background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)' },
  db_fail:    { background: 'rgba(168,85,247,0.15)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.3)' },
  api_fail:   { background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.3)' },
  cron_fail:  { background: 'rgba(249,115,22,0.15)', color: '#fb923c', border: '1px solid rgba(249,115,22,0.3)' },
  other:      { background: 'rgba(107,114,128,0.15)',color: '#9ca3af', border: '1px solid rgba(107,114,128,0.3)' },
};

const SOURCE_COLORS = {
  auto:   { background: 'rgba(34,211,238,0.1)',  color: '#22d3ee', border: '1px solid rgba(34,211,238,0.25)' },
  manual: { background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' },
  api:    { background: 'rgba(16,185,129,0.1)',  color: '#34d399', border: '1px solid rgba(16,185,129,0.25)' },
};

const SOURCE_LABELS = { auto: 'Auto Cron', manual: 'Manual', api: 'API' };

const formatIST = (dt) => {
  if (!dt) return '-';
  return new Date(dt).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
    year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const ErrorLogs = () => {
  const baseURL  = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [logs, setLogs]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(false);
  const [clearing, setClearing]       = useState(false);
  const [filterType, setFilterType]   = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterResolved, setFilterResolved] = useState('false');
  const [expanded, setExpanded]       = useState(null);
  const [resolving, setResolving]     = useState(null);
  const perPage = 50;

  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  const fetchLogs = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate('/'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      if (filterType)     params.append('error_type', filterType);
      if (filterSource)   params.append('source', filterSource);
      if (filterResolved) params.append('resolved', filterResolved);
      const res  = await fetch(`${baseURL}/error_logs/?${params}`, {
        headers: { Authorization: `Token ${token}` },
      });
      const data = await res.json();
      setLogs(data.data  || []);
      setTotal(data.total || 0);
    } catch {
      toastr.error('Failed to load error logs');
    } finally {
      setLoading(false);
    }
  }, [baseURL, navigate, page, filterType, filterSource, filterResolved]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleResolve = async (id) => {
    const token = getToken();
    setResolving(id);
    try {
      const res = await fetch(`${baseURL}/error_logs/${id}/resolve/`, {
        method: 'PATCH',
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolved_by: 'admin' }),
      });
      const data = await res.json();
      if (data.status) {
        toastr.success('Marked as resolved');
        setLogs(prev => prev.map(l => l.id === id ? { ...l, is_resolved: true, resolved_at: new Date().toISOString() } : l));
      }
    } catch {
      toastr.error('Failed to mark as resolved');
    } finally {
      setResolving(null);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear ALL error logs? This cannot be undone.')) return;
    const token = getToken();
    setClearing(true);
    try {
      await fetch(`${baseURL}/clear_error_logs/`, {
        method: 'DELETE', headers: { Authorization: `Token ${token}` },
      });
      setLogs([]); setTotal(0); setPage(1);
      toastr.success('All error logs cleared');
    } catch {
      toastr.error('Failed to clear logs');
    } finally {
      setClearing(false);
    }
  };

  const totalPages = Math.ceil(total / perPage);
  const unresolvedCount = logs.filter(l => !l.is_resolved).length;

  return (
    <div className="tms-page">
      <div className="row g-0">
        <div className="col-auto"><Sidebar /></div>
        <div className="col tms-main">

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              <h2 style={{ color: '#f1f5f9', fontWeight: 800, margin: 0, fontSize: 24 }}>
                ⚠️ Error Logs
                {unresolvedCount > 0 && filterResolved === 'false' && (
                  <span style={{ marginLeft: 10, background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 20, padding: '2px 10px', fontSize: 13 }}>
                    {unresolvedCount} unresolved
                  </span>
                )}
              </h2>
              <p style={{ color: '#4b5563', margin: '4px 0 0', fontSize: 13 }}>
                {total} total • System-wide error tracking across all APIs, crons & functions
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={fetchLogs} disabled={loading} style={btnStyle('#6366f1', 'rgba(99,102,241,0.12)')}>
                {loading ? '⟳ Loading...' : '⟳ Refresh'}
              </button>
              <button onClick={handleClear} disabled={clearing} style={btnStyle('#f87171', 'rgba(239,68,68,0.1)')}>
                {clearing ? 'Clearing...' : '🗑 Clear All'}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={filterResolved} onChange={e => { setFilterResolved(e.target.value); setPage(1); }} style={selectStyle}>
              <option value="">All Status</option>
              <option value="false">⚠ Unresolved</option>
              <option value="true">✅ Resolved</option>
            </select>
            <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} style={selectStyle}>
              <option value="">All Error Types</option>
              {Object.entries(ERROR_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }} style={selectStyle}>
              <option value="">All Sources</option>
              <option value="auto">Auto Cron</option>
              <option value="manual">Manual</option>
              <option value="api">API</option>
            </select>
          </div>

          {/* Table */}
          <div style={{ background: '#13111f', borderRadius: 14, border: '1px solid rgba(255,255,255,0.07)', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['#', 'Status', 'Time (IST)', 'Function / API', 'Error Type', 'Source', 'Ad', 'Lead', 'Phone', 'Error Message', 'Fix', 'Action'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: '50px 0', color: '#818cf8' }}>Loading...</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: '60px 0', color: '#374151', fontSize: 15 }}>
                    {filterResolved === 'false' ? '✅ No unresolved errors — system is healthy!' : 'No logs found'}
                  </td></tr>
                ) : logs.map((log, i) => {
                  const tc = ERROR_TYPE_COLORS[log.error_type] || ERROR_TYPE_COLORS.other;
                  const sc = SOURCE_COLORS[log.source]         || SOURCE_COLORS.api;
                  const isExp = expanded === log.id;
                  return (
                    <tr key={log.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)', opacity: log.is_resolved ? 0.6 : 1 }}>
                      <td style={tdStyle}>{(page - 1) * perPage + i + 1}</td>
                      <td style={tdStyle}>
                        {log.is_resolved
                          ? <span style={{ ...badgeStyle, background: 'rgba(16,185,129,0.15)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}>✅ Resolved</span>
                          : <span style={{ ...badgeStyle, background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' }}>⚠ Open</span>
                        }
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#64748b', fontSize: 12 }}>{formatIST(log.created_at)}</td>
                      <td style={{ ...tdStyle, color: '#818cf8', fontFamily: 'monospace', fontSize: 12, maxWidth: 180 }}>{log.function_name || '-'}</td>
                      <td style={tdStyle}><span style={{ ...badgeStyle, ...tc }}>{ERROR_TYPE_LABELS[log.error_type] || log.error_type}</span></td>
                      <td style={tdStyle}><span style={{ ...badgeStyle, ...sc }}>{SOURCE_LABELS[log.source] || log.source}</span></td>
                      <td style={{ ...tdStyle, color: '#c084fc', maxWidth: 120 }}>{log.ad_name || '-'}</td>
                      <td style={{ ...tdStyle, color: '#e2e8f0' }}>{log.lead_name || '-'}</td>
                      <td style={{ ...tdStyle, color: '#22d3ee', fontFamily: 'monospace' }}>{log.lead_phone || '-'}</td>
                      <td style={{ ...tdStyle, maxWidth: 240 }}>
                        <div
                          onClick={() => setExpanded(isExp ? null : log.id)}
                          title="Click to expand"
                          style={{ color: '#f87171', cursor: 'pointer', lineHeight: 1.5, wordBreak: 'break-word',
                            ...(isExp ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}
                        >
                          {log.error_message}
                        </div>
                        {isExp && log.traceback_info && (
                          <pre style={{ marginTop: 8, background: 'rgba(0,0,0,0.4)', borderRadius: 6, padding: 8, fontSize: 11, color: '#fbbf24', overflowX: 'auto', maxHeight: 200 }}>
                            {log.traceback_info}
                          </pre>
                        )}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: 180, color: '#6ee7b7', fontSize: 12, lineHeight: 1.5 }}>{log.fix_suggestion || '-'}</td>
                      <td style={tdStyle}>
                        {!log.is_resolved ? (
                          <button
                            onClick={() => handleResolve(log.id)}
                            disabled={resolving === log.id}
                            style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#34d399', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            {resolving === log.id ? '...' : '✓ Resolve'}
                          </button>
                        ) : (
                          <span style={{ color: '#374151', fontSize: 12 }}>{formatIST(log.resolved_at)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 20 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={btnStyle('#94a3b8', 'rgba(255,255,255,0.05)')}>← Prev</button>
              <span style={{ color: '#4b5563', fontSize: 13 }}>Page {page} of {totalPages} | {total} total</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={btnStyle('#94a3b8', 'rgba(255,255,255,0.05)')}>Next →</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

const btnStyle = (color, bg) => ({ background: bg, border: `1px solid ${color}40`, color, borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600 });
const selectStyle = { background: '#0d0b1a', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', borderRadius: 8, padding: '8px 12px', fontFamily: 'inherit', fontSize: 13, cursor: 'pointer' };
const thStyle = { padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#4b5563', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.07)', whiteSpace: 'nowrap', background: '#13111f' };
const tdStyle = { padding: '10px 14px', color: '#94a3b8', verticalAlign: 'top' };
const badgeStyle = { display: 'inline-block', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600 };

export default ErrorLogs;
